import { useState } from 'react';
import { useNotify } from 'react-admin';
import { Box, Button, Chip, Typography } from '@mui/material';
import { PermissionGate } from '../PermissionGate';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { shortDateTime } from '../../shared/dateTime';
import { checkEcoTruckConnection, type ConnectionCheck, type ConnectionStatus } from './connectionCheckApi';

/** Kolor chipa wyniku — brak poświadczenia to stan, nie awaria. */
const STATUS_COLOR: Record<ConnectionStatus, 'success' | 'error' | 'warning'> = {
  ok: 'success',
  unauthorized: 'error',
  unreachable: 'error',
  error: 'error',
  noCredential: 'warning',
};

/**
 * Sprawdzenie poświadczeń z ekranu (12.10.2026, decyzja użytkownika „G1”).
 * Na razie eco-truck: jedno lekkie zapytanie bez limitu godzinowego
 * dostawcy (`/fleet/drivers/`), wynik bez klucza i bez treści odpowiedzi.
 * Trans.eu ma „Sprawdź logowanie” w kolejce publikacji; TIMOCOM — bez
 * dostępu do API, więc bez przycisku. Tylko sekcja administracyjna — jak
 * „Sprawdź logowanie” Trans.eu (o dostępie rozstrzyga API).
 */
export const ConnectionChecks = () => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ConnectionCheck | null>(null);
  const check = async () => {
    setBusy(true);
    try {
      setResult(await checkEcoTruckConnection());
    } catch (e) {
      setResult(null);
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <PermissionGate permission="ADMIN_SECTION">
      <Box
        data-testid="connection-checks"
        sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}
      >
        <Typography variant="body2">eco-truck (telematyka):</Typography>
        <Button size="small" variant="outlined" disabled={busy} onClick={() => void check()}>
          Sprawdź połączenie
        </Button>
        {result ? (
          <Chip
            size="small"
            data-testid="connection-check-result"
            data-status={result.status}
            color={STATUS_COLOR[result.status]}
            label={`${result.message} (${shortDateTime(result.checkedAt)})`}
          />
        ) : null}
      </Box>
    </PermissionGate>
  );
};
